/**
 * Free note limit (UI guard).
 *
 * Free accounts created from `NOTE_LIMIT_CUTOFF` on, and local-mode devices
 * first used from that day on, get `FREE_NOTE_LIMIT` active notes. Like
 * `isReadOnly`, the guard is client-side only: the server never rejects a
 * note write, so MCP / REST / Shortcuts creations still land and the banner
 * simply reads "56 of 50". Trashed notes free a slot; archived ones do not.
 *
 * Where the limit comes from:
 *  - Pro (`useIsPro`, which fails open until the entitlement has loaded): none.
 *  - Signed in: the server's `Entitlement.noteLimit` (null for grandfathered
 *    accounts and every account created before the cutoff).
 *  - No account: none while the device carries the legacy marker
 *    (`lib/noteLimit.ts`), none before the cutoff, `FREE_NOTE_LIMIT` after.
 */

import { useEffect } from 'react'
import { FREE_NOTE_LIMIT, NOTE_LIMIT_CUTOFF } from '@caliu/types'
import { useFingerprintedQuery } from '../lib/reactive'
import { queryOne } from '../lib/sqlite'
import { setProfileProps } from '../lib/analytics'
import { isLegacyDevice } from '../lib/noteLimit'
import { OWNED_NOTE_SQL } from '../lib/sharing'
import { useAuthStore } from '../store/auth'
import { useIsPro, useSubscriptionStore } from '../store/subscription'

export interface NoteLimitState {
  /** null = unlimited. */
  limit: number | null
  /** Active + archived notes (everything not in the trash). */
  count: number
  /** No more notes can be created; the paywall opens instead. */
  reached: boolean
  /** 80% of the limit or more: the note list shows the banner. */
  nearLimit: boolean
}

/** Pure: derive the guard state. `count > limit` is legitimate (a device with
 *  300 notes signed into a new account, an MCP agent creating past the cap) and
 *  reads as reached, never as a negative remainder. */
export function computeNoteLimitState(limit: number | null, count: number): NoteLimitState {
  if (limit == null) return { limit, count, reached: false, nearLimit: false }
  return {
    limit,
    count,
    reached: count >= limit,
    nearLimit: count >= Math.ceil(limit * 0.8),
  }
}

/** Pure: which limit applies. `now` / `cutoff` are epoch milliseconds. */
export function resolveNoteLimit({
  hasUser,
  isPro,
  serverLimit,
  legacy,
  now,
  cutoff = Date.parse(NOTE_LIMIT_CUTOFF),
}: {
  hasUser: boolean
  isPro: boolean
  serverLimit: number | null
  legacy: boolean
  now: number
  cutoff?: number
}): number | null {
  if (isPro) return null
  // A device that predates the cutoff keeps its promise when its owner later
  // creates an account: the new account's `created_at` would otherwise cap a
  // long-time local user at the moment they sign up for sync.
  if (legacy) return null
  if (hasUser) return serverLimit
  return now >= cutoff ? FREE_NOTE_LIMIT : null
}

/**
 * Everything not in the trash counts: archive keeps a slot, trash frees it.
 * Only notes this account owns: one shared with it is someone else's
 * (PLAN.md "Rules").
 */
export function countActiveNotes(userId: string | null = useAuthStore.getState().user?.id ?? null): number {
  return (
    queryOne<{ cnt: number }>(
      `SELECT COUNT(*) AS cnt FROM notes WHERE deleted_at IS NULL AND ${OWNED_NOTE_SQL}`,
      [userId ?? ''],
    )?.cnt ?? 0
  )
}

/** Reactive COUNT(*): one cheap query per `notes` write, and a re-render only
 *  when the number itself changes (an autosave never triggers one). */
export function useActiveNoteCount(): number {
  return useFingerprintedQuery<number>({
    tables: ['notes'],
    fingerprint: () => String(countActiveNotes()),
    query: () => countActiveNotes(),
  })
}

/** The limit that applies right now, without the count subscription. */
export function useEffectiveNoteLimit(): number | null {
  const hasUser = useAuthStore((s) => s.user != null)
  const isPro = useIsPro()
  const serverLimit = useSubscriptionStore((s) => s.noteLimit)
  // The marker is written at bootstrap (before React mounts), so a plain read
  // per render is both cheap and always current.
  return resolveNoteLimit({ hasUser, isPro, serverLimit, legacy: isLegacyDevice(), now: Date.now() })
}

export function useNoteLimit(): NoteLimitState {
  const limit = useEffectiveNoteLimit()
  const count = useActiveNoteCount()
  return computeNoteLimitState(limit, count)
}

/**
 * Keeps the `note_limit` profile property (`50` | `'unlimited'`) fresh. Mounted
 * once (`App.tsx`) so there is a single writer. Skipped while a signed-in
 * user's entitlement is still unknown: `useIsPro` fails open then, and that
 * provisional "unlimited" must not reach the profile. Never an event property.
 */
export function useNoteLimitProfile(): void {
  const limit = useEffectiveNoteLimit()
  const hasUser = useAuthStore((s) => s.user != null)
  const loaded = useSubscriptionStore((s) => s.loaded)
  const known = !hasUser || loaded
  useEffect(() => {
    if (!known) return
    setProfileProps({ note_limit: limit ?? 'unlimited' })
  }, [limit, known])
}
